'use client';

import { SegmentGroup } from './segment-group';
import { Menu } from './menu';
import { IconButton } from './icon-button';
import { Text } from './text';
import { automationId } from '@terra/shared-utils';
import { Box, HStack } from '@terra/ui-styled-system';
import { ArrowDown01Icon } from '@terra/ui-icons';
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useSegmentVisibleCount } from './use-segment-visible-count';

export type SegmentGroupHorizontalProps = {
  options: Array<{
    value: string;
    label: string;
    disabled?: boolean;
    dataTestid?: string;
  }>;
  value?: string;
  defaultValue?: string;
  onValueChange?: (details: { value: string | null }) => void;
  maxItemsToDisplay?: number;
};
export const SegmentGroupHorizontal = ({
  options,
  value,
  defaultValue,
  onValueChange,
  maxItemsToDisplay,
}: SegmentGroupHorizontalProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const t = useTranslations('designSystem.menu');
  const { containerRef, measureRef, moreMeasureRef, visibleCount } =
    useSegmentVisibleCount({
      itemCount: options.length,
      maxItemsToDisplay,
    });
  const shouldShowDropdown = options.length > visibleCount;
  const visibleOptions = options.slice(0, visibleCount);
  const hiddenOptions = options.slice(visibleCount);
  const handleMenuSelect = (selectedValue: string) => {
    onValueChange?.({ value: selectedValue });
    setIsMenuOpen(false);
  };

  const isSelected = (optionValue: string) => {
    return (
      value === optionValue ||
      (defaultValue === optionValue && value === undefined)
    );
  };

  const baseItemStyles = {
    borderRadius: 'sm',
    fontSize: 'sm',
    h: '8',
    px: 3,
    borderWidth: 0,
    color: 'fg.muted',
    bg: 'transparent',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as const;
  const selectedStyles = {
    borderWidth: '1px',
    borderColor: 'border.neutral.subtle',
    bg: 'bg.neutral.subtle',
    color: 'fg.default',
  } as const;
  const hasSelectedHiddenOption = hiddenOptions.some((opt) =>
    isSelected(opt.value),
  );
  const moreTriggerContent = (
    <HStack gap={1} alignItems="center">
      <Text fontSize="sm" fontWeight="medium" color="fg.muted">
        {t('more')}
      </Text>
      <ArrowDown01Icon size={16} />
    </HStack>
  );

  return (
    <SegmentGroup.Root
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      borderWidth={0}
      w="full"
      minW={0}
    >
      <Box ref={containerRef} w="full" minW={0} position="relative">
        <HStack
          ref={measureRef}
          gap={1}
          position="absolute"
          visibility="hidden"
          pointerEvents="none"
          aria-hidden
          whiteSpace="nowrap"
        >
          {options.map((option) => (
            <Box key={option.value} {...baseItemStyles} flexShrink={0}>
              <Text fontSize="sm" fontWeight="medium">
                {option.label}
              </Text>
            </Box>
          ))}
        </HStack>
        <Box
          ref={moreMeasureRef}
          {...baseItemStyles}
          position="absolute"
          visibility="hidden"
          pointerEvents="none"
          aria-hidden
          flexShrink={0}
        >
          {moreTriggerContent}
        </Box>

        <HStack gap={1} position="relative" w="full" minW={0}>
          {visibleOptions.map((option) => (
            <SegmentGroup.Item
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              {...automationId(option.dataTestid ?? '')}
              {...baseItemStyles}
              _checked={selectedStyles}
              _hover={{
                bg: 'bg.neutral.hover',
              }}
            >
              <SegmentGroup.ItemControl display="none" />
              <SegmentGroup.ItemText fontWeight="medium">
                {option.label}
              </SegmentGroup.ItemText>
              <SegmentGroup.ItemHiddenInput />
            </SegmentGroup.Item>
          ))}
          {shouldShowDropdown && (
            <Menu.MenuWithItems
              open={isMenuOpen}
              onOpenChange={(details) => setIsMenuOpen(details.open)}
              positioning={{ placement: 'bottom-start', strategy: 'fixed' }}
              items={hiddenOptions.map((option) => ({
                value: option.value,
                label: option.label,
                selected: isSelected(option.value),
                disabled: option.disabled,
                onClick: () => handleMenuSelect(option.value),
              }))}
              trigger={
                <IconButton
                  {...baseItemStyles}
                  aria-label="More options"
                  css={{
                    ...(hasSelectedHiddenOption && selectedStyles),
                  }}
                >
                  <HStack gap={1} alignItems="center">
                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      color={
                        hasSelectedHiddenOption ? 'fg.default' : 'fg.muted'
                      }
                    >
                      {t('more')}
                    </Text>
                    <ArrowDown01Icon size={16} />
                  </HStack>
                </IconButton>
              }
            />
          )}
        </HStack>
      </Box>
    </SegmentGroup.Root>
  );
};
